import type { Element, Root, RootContent, Text } from "hast";

/**
 * Wraps the most recently streamed words in <span data-stream> so CSS can fade
 * them in.
 *
 * Animation eligibility is keyed to POSITION IN THE CONTENT, not to DOM
 * identity. Words behind `cursor` are emitted as plain text, so when react
 * rebuilds a subtree -- which it does constantly while streaming, as incomplete
 * markdown resolves into headings, fences, links and rules -- previously
 * revealed words have no animation to re-fire. Only genuinely new words do.
 *
 * This also keeps the span count proportional to one chunk rather than to the
 * whole message.
 *
 * Runs LAST in the pipeline: after rehype-sanitize, so the sanitizer never sees
 * these spans and its schema needs no widening, and after rehype-katex, so
 * rendered math can be skipped wholesale.
 */

/* Splitting inside these would corrupt their contents. */
const SKIP_TAGS = new Set(["code", "pre", "style", "script", "textarea", "math"]);

export type StreamCursor = { current: number };
export type StreamStats = { total: number };

interface Options {
  /** Words at an index below this are already revealed and render inert. */
  cursor: StreamCursor;
  /** Receives the word count of this pass so the caller can advance the cursor. */
  stats: StreamStats;
}

const isKatex = (node: Element): boolean => {
  const className = node.properties?.className;
  if (Array.isArray(className)) return className.some((c) => String(c).startsWith("katex"));
  if (typeof className === "string") return className.startsWith("katex");
  return false;
};

const rehypeStreamWords = ({ cursor, stats }: Options) => (tree: Root) => {
  let index = 0;

  const splitTextNode = (node: Text): RootContent[] =>
    node.value
      .split(/(\s+)/)
      .filter((part) => part !== "")
      .map((part) => {
        if (/^\s+$/.test(part)) return { type: "text", value: part } as Text;

        const isNew = index >= cursor.current;
        index += 1;

        // Already-revealed words stay plain text -- nothing for a remount to animate.
        if (!isNew) return { type: "text", value: part } as Text;

        return {
          type: "element",
          tagName: "span",
          properties: { "data-stream": "" },
          children: [{ type: "text", value: part } as Text],
        } as Element;
      });

  const walk = (node: Root | Element) => {
    if (!node.children?.length) return;

    const next: RootContent[] = [];

    for (const child of node.children) {
      if (child.type === "text") {
        next.push(...splitTextNode(child));
        continue;
      }

      if (child.type === "element" && (SKIP_TAGS.has(child.tagName) || isKatex(child))) {
        next.push(child);
        continue;
      }

      if (child.type === "element") walk(child);
      next.push(child);
    }

    node.children = next;
  };

  walk(tree);

  // Idempotent write: a StrictMode double render produces the same value.
  stats.total = index;
};

export default rehypeStreamWords;
